import { useCallback, useReducer, useRef } from 'react';
import useMemberCyclePurchaseApi from '@Apis/useMemberCyclePurchaseApi';
import { EVENTS, INIT, reduce } from './cyclePurchaseDetailReducer';

/**
 * 循環訂單「預約變更」卡片內就地展開詳情的薄殼 hook。
 * 包裝 cyclePurchaseDetailReducer.js 的純 reducer，負責：
 * - 呼叫變更單詳情 API（帶 { isLoadingVisible: false }，不觸發全頁 loading 遮罩）
 * - 用 useRef 持有的 in-flight effectID 集合做請求去重（唯一依據，不可用 loadingIds，見 spec D8）
 * - 從 reducer state 讀出該 effectID 當前 token 隨請求帶出，settle 後原封不動放進 LOAD_RESOLVED payload
 *
 * 責任邊界（釘死，勿破壞）：
 * - 本 hook 不做任何成功／失敗判定：請求 settle 後只派發單一事件 LOAD_RESOLVED，
 *   payload 為 { effectID, token, data }，data 為 useAxios 原封不動的回傳值。
 * - 本 hook 不生成、不遞增、不比較 token，只單純讀取 reducer state 目前的值。
 * - effectID 一律使用呼叫端傳入的 record.effectID（原始數值），不可從 map key 反查。
 *
 * @param {number} cycleOrderID
 */
const useCyclePurchaseEffectDetails = (cycleOrderID) => {
    const [state, dispatch] = useReducer((prevState, action) => reduce(prevState, action.type, action.payload), INIT);
    const { getMemberCyclePurchaseEffectionApi } = useMemberCyclePurchaseApi();

    // 去重的唯一依據：同步 ref，發請求前檢查、發請求時加入、settle 後移除
    const inFlightIdsRef = useRef(new Set());

    // 功能：展開／收合切換。已展開 → 收合（快取保留）；未展開 → 進入 REQUEST_EXPAND，
    // 無快取且未在 in-flight 中才實際呼叫 API
    const toggle = useCallback(
        (record) => {
            const effectID = record?.effectID;
            if (!effectID) return;

            if (state.expandedIds[effectID]) {
                dispatch({ type: EVENTS.COLLAPSE, payload: { effectID } });
                return;
            }

            const hasCache = Object.prototype.hasOwnProperty.call(state.detailMap, effectID);
            dispatch({ type: EVENTS.REQUEST_EXPAND, payload: { effectID } });

            if (hasCache) return;

            // 去重：發請求前檢查 in-flight 集合（唯一依據，不可用 loadingIds，見規格 D8 token 交接契約）
            if (inFlightIdsRef.current.has(effectID)) return;
            inFlightIdsRef.current.add(effectID);

            // 從 reducer state 讀出該筆目前的 token，原封不動隨請求帶著，不生成、不比較
            const token = state.tokens[effectID] || 0;

            getMemberCyclePurchaseEffectionApi(cycleOrderID, effectID, { isLoadingVisible: false })
                .then((data) => {
                    dispatch({ type: EVENTS.LOAD_RESOLVED, payload: { effectID, token, data } });
                })
                .catch(() => {
                    // useAxios 內部已包 try/catch、正常不會 reject；此處為防禦：
                    // 若真的 reject 而不派發事件，該筆會永遠留在 loadingIds，
                    // 導致 REQUEST_EXPAND 一律 no-op、使用者再也無法重試。
                    // 派 data: undefined 讓 reducer 走失敗路徑（離開載入中、不寫快取），保留重試能力。
                    dispatch({ type: EVENTS.LOAD_RESOLVED, payload: { effectID, token, data: undefined } });
                })
                .finally(() => {
                    inFlightIdsRef.current.delete(effectID);
                });
        },
        // eslint-disable-next-line
        [state, cycleOrderID, getMemberCyclePurchaseEffectionApi],
    );

    // 功能：清除指定 effectID 的展開狀態、載入中、快取（刪除成功後呼叫）
    const clear = useCallback((effectID) => {
        if (!effectID) return;
        dispatch({ type: EVENTS.CLEAR, payload: { effectID } });
    }, []);

    return {
        expandedIds: state.expandedIds,
        loadingIds: state.loadingIds,
        detailMap: state.detailMap,
        toggle,
        clear,
    };
};

export default useCyclePurchaseEffectDetails;
